// The ledger's total is the one number on this page that can't come from
// Liquid: it depends on which colourway is showing in each column, and there
// are colours-squared combinations of those. This is the theme's own money
// formatter, the same one assets/facets.js uses for its price filter, fed the
// same two inputs from data attributes — so no money string is ever parsed
// back into a number and a Markets buyer sees their own currency.
import { formatMoney } from '@theme/money-formatting';
import { WheelIntent, wheelDeltaPx } from '@theme/wheel-intent';

// Matches the sheet's transform transition in section-shabibi-lookbuilder.css.
// Only decides when the closed sheet stops being rendered; .is-open is what
// takes it out of the hit-test path, so being a little long here is harmless.
const SHEET_TRANSITION_MS = 380;

// Matches .lookbuilder-guide-view's own (unmodified) transition duration in
// section-shabibi-lookbuilder.css — the guide's CLOSE speed, since its OPEN
// duration only applies while .is-guide-open is present. Same "a little long
// is harmless" reasoning as SHEET_TRANSITION_MS: this only decides when the
// guide view stops being rendered underneath its own transform.
const GUIDE_CLOSE_TRANSITION_MS = 240;

// Byte-identical to the sheet block in section-shabibi-lookbuilder.css and to
// the inline mode script in the section. Three branches, one idea: the ledger
// is a modal overlay sheet wherever the stage can't also hold a 464px panel
// beside the look — narrow (phone), portrait up to 1024px (tablet), or simply
// too short (any phone on its side, and equally a short desktop window).
// Above all three it's a panel sharing the screen instead.
//
// That difference is why `hidden` and the focus trap are conditional — a panel
// that shares the screen shouldn't be removed from the page or trap the tab
// key, and an overlay that covers it must do both. The short-landscape branch
// needs nothing new here: it's still an overlay sheet, just with the garments
// laid out in a row behind it, which is decided entirely in CSS.
const SHEET_LAYOUT_QUERY =
  '(max-width: 767px), (max-width: 1024px) and (orientation: portrait), (max-height: 599px) and (orientation: landscape)';

// Clones per end of each colourway carousel, faking an infinite loop on
// mobile the same way assets/shabibi-peek-carousel.js does for the shop
// page. Each .lookbuilder-slide is flex: 0 0 100% (one at a time, no peek),
// so unlike that carousel's variable viewport-relative count, a flat 2 is
// plenty of runway for a single fling to land on.
const COLORWAY_LOOP_CLONE_COUNT = 2;

// Desktop colourway slide (see animateColorwaySlide). Ease-out so a step
// answers the swipe immediately and settles softly.
const COLORWAY_SLIDE_MS = 400;
const COLORWAY_SLIDE_EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';

// Copy for the add-to-cart button, in one place. There used to be four
// writers between updateButtonState() and handleAddBundle(), each setting an
// aria-label directly; adding the mobile sheet's button would have made it
// eight. Callers say which state the look is in and never touch the text —
// the same discipline shabibi-cta.js settled on for the PDP.
const CTA_COPY = {
  idle: 'Select your sizes',
  armed: 'Add look to cart',
  soldout: 'Sold out',
  busy: 'Adding…',
  done: 'Added',
  error: 'Try again',
};

class ShabibiLookbuilder extends HTMLElement {
  constructor() {
    super();
    this.addBundleBtn = this.querySelector('.lookbuilder-add-bundle');
    this.carousels = this.querySelectorAll('.lookbuilder-column');
    this.state = {
      column1_variant: null,
      column2_variant: null
    };

    // The size the shopper last picked per column, kept by VALUE rather than
    // by element. A colourway is a separate product here, so switching colour
    // swaps in a different slide with a different, entirely unchecked set of
    // size radios — without this, choosing a colour after a size silently
    // discarded the size and the chip fell back to "Size".
    this.carriedSize = { 1: null, 2: null };

    // Which slide each column's proxies were last built from, so a re-sync
    // that changes nothing doesn't rebuild the buttons under the shopper's
    // finger and drop focus.
    this.renderedSlide = { 1: null, 2: null };

    // The active colourway per column, held as the element rather than
    // re-derived from the DOM. It cannot be found by querying the track for
    // `.lookbuilder-color-radio:checked`: every slide carries its OWN full
    // swatch set as its own radio group with its own product pre-checked, so
    // that query always returns the first slide's radio no matter which
    // colourway is showing. Every path into a colourway funnels through
    // updateVariantForSlide, so recording it there is enough.
    this.activeSlideEl = { 1: null, 2: null };

    // Desktop colourway slide state per column: the direction the next
    // change should travel (set by stepColorway, which knows it even across
    // the last→first wrap, where the indices alone would say "backwards"),
    // and the slides currently mid-animation, so a new step can pick them
    // up where they are rather than jumping.
    this.pendingSlideDir = { 1: 0, 2: 0 };
    this.slideAnims = { 1: new Map(), 2: new Map() };
    this.sheetLayoutQuery = window.matchMedia(SHEET_LAYOUT_QUERY);
    this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.initEvents();
    this.initSheet();
    // After both, not at the end of initEvents: syncSheet needs the
    // references initSheet resolves, so calling it earlier silently did
    // nothing. Mobile never showed it — opening the sheet re-syncs first —
    // but the desktop panel and its entry card are on screen from load.
    this.syncSheet();

    // Last: it reads the sheet's open state and the active slide, which the
    // two calls above resolve.
    this.swipeHint = new ColorwaySwipeHint(this);
  }

  initEvents() {
    // Handle swatch click explicitly
    this.addEventListener('click', (e) => {
      const colorwayBtn = e.target.closest('[data-lb-colorway-prev], [data-lb-colorway-next]');
      if (colorwayBtn) {
        const column = colorwayBtn.dataset.column;
        const direction = colorwayBtn.hasAttribute('data-lb-colorway-prev') ? -1 : 1;
        this.stepColorway(column, direction);
        return;
      }

      const swatch = e.target.closest('.lookbuilder-swatch');
      if (swatch) {
        const forId = swatch.getAttribute('for');
        const radio = forId ? this.querySelector(`#${CSS.escape(forId)}`) : null;
        if (radio) {
          radio.checked = true;
          const colEl = radio.closest('.lookbuilder-column');
          const column = colEl.dataset.column;
          const track = colEl.querySelector('.lookbuilder-carousel');
          const slideIndex = radio.dataset.slideIndex;

          if (track && slideIndex !== undefined) {
            const targetSlide = this.findRealSlide(track, slideIndex);
            if (targetSlide) {
              track.scrollTo({
                left: targetSlide.offsetLeft,
                behavior: 'smooth'
              });
              this.selectSlide(column, targetSlide);
            }
          }
        }
      }
    });

    // Handle option changes (both color radio and size radio)
    this.addEventListener('change', (e) => {
      if (e.target.matches('.lookbuilder-color-radio')) {
        const colEl = e.target.closest('.lookbuilder-column');
        const column = colEl.dataset.column;
        const track = colEl.querySelector('.lookbuilder-carousel');
        const slideIndex = e.target.dataset.slideIndex;

        if (track && slideIndex !== undefined) {
          const targetSlide = this.findRealSlide(track, slideIndex);
          if (targetSlide) {
            track.scrollTo({
              left: targetSlide.offsetLeft,
              behavior: 'smooth'
            });
            this.selectSlide(column, targetSlide);
            return;
          }
        }
      }

      if (e.target.matches('.lookbuilder-size-radio')) {
        const colEl = e.target.closest('.lookbuilder-column');
        const column = colEl.dataset.column;
        const slide = e.target.closest('.lookbuilder-slide');

        const groupRadios = slide.querySelectorAll(`input[name="${e.target.name}"]`);
        groupRadios.forEach(radio => {
          const label = slide.querySelector(`label[for="${radio.id}"]`);
          if (label) {
            label.classList.toggle('is-active', radio.checked);
          }
        });

        // Remember it by value so it survives a colourway change.
        this.carriedSize[column] = e.target.value;

        this.updateVariantForSlide(column, slide);
        this.syncSheet();
      }
    });

    // Handle add bundle
    if (this.addBundleBtn) {
      this.addBundleBtn.addEventListener('click', this.handleAddBundle.bind(this));
    }

    // Desktop-only: a two-finger horizontal trackpad swipe over a garment
    // steps its colourway, same action as the < > arrows beside it. Gated
    // on !sheetLayoutQuery.matches rather than a separate desktop query —
    // SHEET_LAYOUT_QUERY already covers both mobile layouts (A and B), and
    // its complement is exactly the desktop stage (C). Not needed on mobile:
    // the < > arrows are hidden there too (CLAUDE.md: "Mobile doesn't need
    // it, swiping the carousel already works there").
    const sheetLayoutQueryForWheel = window.matchMedia(SHEET_LAYOUT_QUERY);

    // Handle arrows and scroll logic
    this.carousels.forEach(col => {
      const imageCarousels = col.querySelectorAll('.image-carousel-container');
      imageCarousels.forEach(container => {
        const imgTrack = container.querySelector('.lookbuilder-image-carousel');
        const prevBtn = container.querySelector('.lookbuilder-arrow--prev');
        const nextBtn = container.querySelector('.lookbuilder-arrow--next');
        if (prevBtn && imgTrack) {
          prevBtn.addEventListener('click', () => {
            if (imgTrack.scrollLeft <= 0) {
              imgTrack.scrollTo({ left: imgTrack.scrollWidth, behavior: 'smooth' });
            } else {
              imgTrack.scrollBy({ left: -imgTrack.clientWidth, behavior: 'smooth' });
            }
          });
        }
        if (nextBtn && imgTrack) {
          nextBtn.addEventListener('click', () => {
            if (Math.ceil(imgTrack.scrollLeft + imgTrack.clientWidth) >= imgTrack.scrollWidth) {
              imgTrack.scrollTo({ left: 0, behavior: 'smooth' });
            } else {
              imgTrack.scrollBy({ left: imgTrack.clientWidth, behavior: 'smooth' });
            }
          });
        }
      });
      
      // A column whose collection is empty (or unset) renders a placeholder
      // instead of a carousel, so there is no track. Bailing here rather than
      // guarding each use: without this the read below threw, and because it
      // threw inside the forEach it took the rest of initEvents with it —
      // one misconfigured collection in the theme editor left the whole
      // component dead, add-to-cart included.
      const track = col.querySelector('.lookbuilder-carousel');
      if (!track) return;

      this.initColorwayWheel(col, track, sheetLayoutQueryForWheel);

      this.setupColorwayLoopClones(track);

      // Update selected variant when sliding to a new product
      let isScrolling;
      track.addEventListener('scroll', () => {
        window.clearTimeout(isScrolling);
        isScrolling = setTimeout(() => {
          this.handleScrollEnd(col, track);
        }, 150);
      });

      // Initialize variant for the first visible slide. :not([data-clone])
      // matters here specifically: setupColorwayLoopClones prepends head
      // clones before the real first slide, so a bare .lookbuilder-slide
      // query would otherwise match one of those instead.
      const firstSlide = track.querySelector('.lookbuilder-slide:not([data-clone])');
      if (firstSlide) {
        this.updateVariantForSlide(col.dataset.column, firstSlide);
      }
    });
  }

  /**
   * Clone the first/last COLORWAY_LOOP_CLONE_COUNT real slides onto the
   * opposite end of the track, so a mobile swipe past the last colourway
   * reveals a clone of the first one (and vice versa) instead of hitting a
   * wall. Once the scroll settles on a clone, handleScrollEnd silently (no
   * animation) repositions onto the equivalent real slide, so the runway
   * never runs out. Same technique as the shop page's peek carousel
   * (assets/shabibi-peek-carousel.js setupLoopClones/resolveLoopClone) —
   * see sanitizeClone() below for why this carousel's clones need more care
   * than that one's plain-<img> clones.
   *
   * Runs unconditionally rather than gated to the mobile breakpoint: on
   * desktop .lookbuilder-slide is absolutely stacked and slides (see
   * section-shabibi-lookbuilder.css and animateColorwaySlide), and a clone
   * never receives .is-active or an animation (only updateVariantForSlide
   * does either, always with a real slide — see
   * findRealSlide/activeSlide), so it simply sits invisibly at opacity 0
   * there. Simpler than adding/removing clones on every breakpoint change.
   */
  setupColorwayLoopClones(track) {
    const realSlides = Array.from(track.children);
    if (realSlides.length < 2) return;

    const cloneCount = Math.min(COLORWAY_LOOP_CLONE_COUNT, realSlides.length - 1);

    const headClones = realSlides.slice(-cloneCount).map((slide, i) => {
      const clone = slide.cloneNode(true);
      this.sanitizeClone(clone, `head${i}`);
      clone.dataset.clone = 'true';
      return clone;
    });
    const tailClones = realSlides.slice(0, cloneCount).map((slide, i) => {
      const clone = slide.cloneNode(true);
      this.sanitizeClone(clone, `tail${i}`);
      clone.dataset.clone = 'true';
      return clone;
    });

    headClones.forEach((clone) => track.insertBefore(clone, track.firstChild));
    tailClones.forEach((clone) => track.appendChild(clone));

    // Head clones landed before the first real slide, so the track's
    // default scroll position (0) would now show one of them instead of
    // the true first colourway — reassert it once layout has settled.
    requestAnimationFrame(() => {
      track.scrollTo({ left: realSlides[0].offsetLeft, behavior: 'auto' });
    });
  }

  /**
   * Neutralizes everything cloneNode(true) copies verbatim that could
   * collide with the real slide a clone stands in for: ids (and the
   * labels' `for` pointing at them) and radio `name` groups. Radios
   * sharing a `name` are one group document-wide regardless of which
   * slide element they live in (the same fact the mobile sheet's proxies
   * are built around — see the note on activateProxy), so an unrenamed
   * clone of a color radio would silently join, and could uncheck, the
   * real slide's own group the instant both are in the DOM together. An
   * unrenamed id would make every this.querySelector('#...') in this file
   * that expects the real element return the CLONE instead, since
   * querySelector returns the first match in document order and head
   * clones sit before the real slides. Clones are decorative only — never
   * meant to be interacted with, only scrolled past — so on top of the
   * renaming, every input inside one is disabled.
   */
  sanitizeClone(clone, tag) {
    clone.querySelectorAll('[id]').forEach((el) => {
      const oldId = el.id;
      const newId = `${oldId}--clone-${tag}`;
      clone.querySelectorAll(`[for="${CSS.escape(oldId)}"]`).forEach((label) => {
        label.setAttribute('for', newId);
      });
      el.id = newId;
    });
    clone.querySelectorAll('input[name]').forEach((input) => {
      input.name = `${input.name}--clone-${tag}`;
      input.disabled = true;
    });
  }

  /** The real (non-clone) slide in `track` carrying this data-slide-index. */
  findRealSlide(track, index) {
    return track.querySelector(`:scope > .lookbuilder-slide[data-slide-index="${index}"]:not([data-clone])`);
  }

  /**
   * Step the desktop entry stage to the next/previous colourway.
   *
   * Reuses the existing .lookbuilder-color-radio 'change' handler rather than
   * duplicating its scroll/selectSlide logic: every slide carries the full
   * sibling set as its own radio group (see the note on activeSlideEl), so
   * the CURRENT slide always has a radio pointing at whatever index comes
   * next — checking it and dispatching 'change' is enough to drive the same
   * path a swatch click takes.
   *
   * @param {string} column - '1' or '2'.
   * @param {number} direction - -1 for previous, 1 for next.
   */
  stepColorway(column, direction) {
    const col = this.querySelector(`.lookbuilder-column[data-column="${column}"]`);
    const track = col?.querySelector('.lookbuilder-carousel');
    if (!track) return;

    // Real slides only — track.children now also holds the loop clones
    // setupColorwayLoopClones adds, which would throw indexOf/modulo off.
    const slides = Array.from(track.querySelectorAll(':scope > .lookbuilder-slide:not([data-clone])'));
    if (slides.length < 2) return;

    const current = this.activeSlide(column) || slides[0];
    const currentIndex = slides.indexOf(current);
    const nextIndex = (currentIndex + direction + slides.length) % slides.length;

    const radio = current.querySelector(`.lookbuilder-color-radio[data-slide-index="${nextIndex}"]`);
    if (!radio) return;

    // Consumed synchronously by updateVariantForSlide inside the dispatch
    // below, then cleared in case the change didn't reach it.
    this.pendingSlideDir[column] = direction;
    radio.checked = true;
    radio.dispatchEvent(new Event('change', { bubbles: true }));
    this.pendingSlideDir[column] = 0;
  }

  /**
   * Wires a two-finger horizontal trackpad swipe over `col` (desktop only)
   * to step that column's colourway — the same action stepColorway() already
   * drives for the < > arrows. A trackpad swipe fires a burst of wheel
   * events (the swipe plus a decaying tail of momentum ticks), so a naive
   * "one event, one step" handler walks through several colourways from one
   * gesture. WheelIntent (assets/shabibi-wheel-intent.js, shared with the
   * PDP media carousel) turns that burst into one step per swipe, and
   * recognises a new swipe during the tail rather than waiting for it to go
   * quiet, so quick repeat swipes aren't swallowed.
   *
   * Unlike the PDP carousel, steps aren't held back until the previous one
   * finishes: animateColorwaySlide picks up any slide still mid-animation
   * from where it is, so stepping mid-slide just carries on smoothly.
   *
   * @param {HTMLElement} col - The .lookbuilder-column element.
   * @param {HTMLElement} track - Its .lookbuilder-carousel.
   * @param {MediaQueryList} sheetLayoutQuery - Matches on the mobile/sheet
   *   layouts (A and B); this feature is for their complement, the desktop
   *   stage (C), only.
   */
  initColorwayWheel(col, track, sheetLayoutQuery) {
    const intent = new WheelIntent();

    track.addEventListener(
      'wheel',
      (e) => {
        if (sheetLayoutQuery.matches) return;

        // Vertical-dominant events are left alone so the page still scrolls
        // under the pointer. Every horizontal one is cancelled, however
        // small: Chrome and Safari only make the first wheel event of a
        // trackpad sequence cancelable, and a swipe opens with a 1–4px tick,
        // so filtering by size before preventDefault() (as an earlier
        // version did) left the rest of the swipe uncancellable — free to
        // trigger the browser's back/forward swipe navigation.
        if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
        e.preventDefault();

        const delta = wheelDeltaPx(e.deltaX, e.deltaMode, track.clientWidth);
        const dir = intent.feed(delta, e.timeStamp);
        if (dir) this.stepColorway(col.dataset.column, dir);
      },
      { passive: false }
    );
  }

  /**
   * Make a slide the active colourway for its column.
   *
   * Two resolution passes on purpose. Availability is computed inside
   * updateVariantForSlide (it's what disables the sold-out radios), so the
   * carried size can only be checked for availability after the first pass —
   * and once it's restored the variant has to be resolved again with it.
   *
   * @param {string} column - '1' or '2'.
   * @param {HTMLElement} slide - The slide to make active.
   */
  selectSlide(column, slide) {
    this.updateVariantForSlide(column, slide);
    if (this.applyCarriedSize(column, slide)) {
      this.updateVariantForSlide(column, slide);
    }
    this.syncSheet();
  }

  /**
   * Bring the column's remembered size onto a newly active slide.
   *
   * Deliberately authoritative rather than a fallback. Each slide's radios
   * keep their own checked state, so a colourway the shopper has already
   * visited remembers what they picked there — which means without this,
   * which size shows depends on whether you've seen that colourway before.
   * Two memories of one choice, and the winner decided by history. Here the
   * shopper's most recent stated size wins everywhere: it's applied if the
   * new colourway stocks it, and the slide is cleared if it doesn't, so the
   * chip falls back to "Size" and asks honestly instead of quietly showing
   * a size they chose several taps ago.
   *
   * @returns {boolean} Whether the slide's selection changed.
   */
  applyCarriedSize(column, slide) {
    const want = this.carriedSize[column];
    if (!want) return false;

    const radios = Array.from(slide.querySelectorAll('.lookbuilder-size-radio'));
    const match = radios.find((radio) => radio.value === want && !radio.disabled);

    let changed = false;
    radios.forEach((radio) => {
      const shouldCheck = radio === match;
      if (radio.checked !== shouldCheck) changed = true;
      radio.checked = shouldCheck;

      const label = slide.querySelector(`label[for="${CSS.escape(radio.id)}"]`);
      if (label) label.classList.toggle('is-active', shouldCheck);
    });

    return changed;
  }

  updateVariantForSlide(column, slide) {
    const previous = this.activeSlideEl[column];
    this.activeSlideEl[column] = slide;

    // Decides which slide shows on desktop (.lookbuilder-slide.is-active in
    // section-shabibi-lookbuilder.css). Every path into a colourway change —
    // swatch click, radio change, stepColorway — funnels through here, so
    // this is the one place that needs to know which slide is active,
    // matching the same reasoning as activeSlideEl above. Harmless on
    // mobile: nothing there styles .is-active, so this is a no-op class.
    const track = slide.parentElement;
    if (track) {
      Array.from(track.children).forEach((child) => {
        child.classList.toggle('is-active', child === slide);
      });

      if (previous && previous !== slide && previous.parentElement === track) {
        const direction =
          this.pendingSlideDir[column] ||
          Math.sign(Number(slide.dataset.slideIndex) - Number(previous.dataset.slideIndex)) ||
          1;
        this.pendingSlideDir[column] = 0;
        this.animateColorwaySlide(column, track, previous, slide, direction);
      }
    }

    const jsonEl = slide.querySelector('.lookbuilder-variants-json');
    if (!jsonEl) return;
    
    let variants = [];
    try {
      variants = JSON.parse(jsonEl.textContent);
    } catch (e) {
      return;
    }
    
    // Gather selected size options
    const selectedOptions = {};
    
    // Check size rows
    const sizeRows = slide.querySelectorAll('.lookbuilder-size-row');
    sizeRows.forEach(row => {
      const optionIndex = row.dataset.optionIndex;
      const checked = row.querySelector('.lookbuilder-size-radio:checked');
      if (checked) selectedOptions[optionIndex] = checked.value;
    });

    const totalOptions = sizeRows.length;

    // Update availability styling for size rows
    sizeRows.forEach(row => {
      const rowOptionIndex = row.dataset.optionIndex;
      const radios = row.querySelectorAll('.lookbuilder-size-radio');
      radios.forEach(radio => {
        const val = radio.value;
        const label = slide.querySelector(`label[for="${radio.id}"]`);
        const isAvailable = variants.some(v => {
          if (v[rowOptionIndex] !== val) return false;
          if (!v.available) return false;
          return Array.from(sizeRows).every(otherRow => {
            if (otherRow === row) return true;
            const otherOptIdx = otherRow.dataset.optionIndex;
            if (selectedOptions[otherOptIdx]) {
              return v[otherOptIdx] === selectedOptions[otherOptIdx];
            }
            return true;
          });
        });

        radio.disabled = !isAvailable;
        if (label) {
          label.classList.toggle('is-sold-out', !isAvailable);
        }
      });
    });
    
    // Find matching variant
    let matchedVariant = null;
    if (Object.keys(selectedOptions).length === totalOptions && totalOptions > 0) {
      matchedVariant = variants.find(variant => {
        return Object.keys(selectedOptions).every(key => variant[key] === selectedOptions[key]);
      });
    }
    
    if (matchedVariant) {
      this.state[`column${column}_variant`] = matchedVariant.id;
      this.state[`column${column}_available`] = matchedVariant.available;
      
      // Image scrolling logic
      if (matchedVariant.featured_image && matchedVariant.featured_image.src) {
        const carousel = slide.querySelector('.lookbuilder-image-carousel');
        if (carousel) {
          const featuredFilename = matchedVariant.featured_image.src.split('?')[0].split('/').pop();
          const targetImage = Array.from(carousel.querySelectorAll('.lookbuilder-product-image')).find(img => {
            const imgSrc = img.src.split('?')[0].split('/').pop();
            return imgSrc === featuredFilename || img.src.includes(matchedVariant.featured_image.src.split('?')[0]);
          });
          if (targetImage) {
            carousel.scrollTo({ left: targetImage.offsetLeft - carousel.offsetLeft, behavior: 'smooth' });
          }
        }
      }
    } else {
      this.state[`column${column}_variant`] = null;
      this.state[`column${column}_available`] = false;
    }
    
    this.updateButtonState();
  }

  handleScrollEnd(col, track) {
    // Real slides AND clones — the point of this pass is finding whatever
    // the user actually scrolled to, which on a fling past either end is a
    // clone by design.
    const slides = Array.from(track.children);
    let visibleSlide = slides[0];
    let minDistance = Infinity;
    const center = track.scrollLeft + track.clientWidth / 2;

    slides.forEach(slide => {
      const slideCenter = slide.offsetLeft + slide.clientWidth / 2;
      const distance = Math.abs(center - slideCenter);
      if (distance < minDistance) {
        minDistance = distance;
        visibleSlide = slide;
      }
    });

    // Settled on a clone: jump (no animation) to the real slide it stands
    // in for before doing anything else with it. selectSlide/
    // updateVariantForSlide must never be handed a clone — activeSlideEl
    // would end up holding it, and every downstream id-based lookup
    // (renderProxies, activateProxy, etc.) assumes the real element.
    if (visibleSlide.dataset.clone === 'true') {
      const realSlide = this.findRealSlide(track, visibleSlide.dataset.slideIndex);
      if (realSlide) {
        track.scrollTo({ left: realSlide.offsetLeft, behavior: 'auto' });
        visibleSlide = realSlide;
      }
    }

    const colId = col.dataset.column;
    this.selectSlide(colId, visibleSlide);
  }

  updateButtonState() {
    const both = this.state.column1_variant && this.state.column2_variant;
    if (!both) {
      this.setCtaState('idle');
    } else if (!this.state.column1_available || !this.state.column2_available) {
      this.setCtaState('soldout');
    } else {
      this.setCtaState('armed');
    }
  }

  /**
   * The single writer for both add-to-cart triggers — the desktop ✛ and the
   * mobile sheet's button. The ✛ is an icon, so it takes the copy as its
   * accessible name; the sheet's button shows it.
   *
   * @param {keyof CTA_COPY} next
   */
  setCtaState(next) {
    const copy = this.ctaCopy?.[next] || CTA_COPY[next] || CTA_COPY.idle;
    const enabled = next === 'armed';

    if (this.addBundleBtn) {
      this.addBundleBtn.disabled = !enabled;
      this.addBundleBtn.setAttribute('aria-label', copy);
    }

    if (this.sheetAddBtn) {
      this.sheetAddBtn.disabled = !enabled;
      this.sheetAddBtn.textContent = copy;
    }
  }

  /* ═══════════════════════════════════════════════════════════════════
     MOBILE LEDGER SHEET

     The sheet's swatches and sizes are PROXIES, not a second set of radios.
     Tapping one checks the real radio inside the active slide and fires
     change, so the whole existing resolution path — availability marking,
     variant matching, featured-image swap, button state — runs untouched.

     Duplicating the radios instead is the obvious move and it's a trap:
     radios sharing a `name` are one group document-wide, so checking a copy
     in the sheet UNCHECKS the one in the slide, and updateVariantForSlide
     resolves by reading `:checked` scoped to that slide. It would return
     null and the variant would never resolve.
     ═══════════════════════════════════════════════════════════════════ */

  initSheet() {
    this.sheetRoot = this.querySelector('[data-lb-sheet-root]');
    if (!this.sheetRoot) return;

    this.sheet = this.sheetRoot.querySelector('[data-lb-sheet]');
    this.sheetAddBtn = this.sheetRoot.querySelector('[data-lb-add]');
    this.sheetOpenBtn = this.querySelector('[data-lb-open]');
    this.sheetLastFocused = null;

    // Merchant-editable labels, read once from the button's own data
    // attributes (sourced from section settings) rather than re-read on every
    // state change — setCtaState() stays the only writer of the button text,
    // this only changes where its defaults come from. Falls back to the
    // hardcoded English copy if the markup predates these attributes.
    this.ctaCopy = {
      ...CTA_COPY,
      idle: this.sheetAddBtn?.dataset.labelIdle || CTA_COPY.idle,
      armed: this.sheetAddBtn?.dataset.labelArmed || CTA_COPY.armed,
      soldout: this.sheetAddBtn?.dataset.labelSoldout || CTA_COPY.soldout,
    };

    this.sheetOpenBtn?.addEventListener('click', () => this.openSheet());
    this.sheetRoot.querySelector('[data-lb-sheet-scrim]')?.addEventListener('click', () => this.closeSheet());
    this.sheetAddBtn?.addEventListener('click', () => this.handleAddBundle());
    // Desktop's own way out; mobile dismisses by scrim, drag or Escape.
    this.querySelector('[data-lb-close]')?.addEventListener('click', () => this.closeSheet());

    // The size guide is a second VIEW inside this same sheet, not a second
    // sheet — see the markup comment above .lookbuilder-guide-view. The
    // trigger keeps its href as a no-JS fallback to a real page, so the
    // click has to be told not to navigate.
    this.guideView = this.sheetRoot.querySelector('[data-lb-guide-view]');
    this.guideOpenLink = this.sheetRoot.querySelector('[data-lb-guide-open]');
    this.guideBackBtn = this.sheetRoot.querySelector('[data-lb-guide-back]');
    this.guideOpenLink?.addEventListener('click', (e) => {
      e.preventDefault();
      this.openGuide();
    });
    this.guideBackBtn?.addEventListener('click', () => this.closeGuide());

    // Chips and proxies are all inside the sheet and all rebuilt on sync, so
    // both are handled by delegation rather than per-element binding.
    this.sheetRoot.addEventListener('click', (e) => {
      const chip = e.target.closest('[data-lb-chip]');
      if (chip) {
        this.toggleDrawer(chip);
        return;
      }

      const proxy = e.target.closest('[data-lb-proxy]');
      if (proxy) this.activateProxy(proxy);
    });

    // Hovering a ledger row fades the other garment, so it's obvious which
    // half of the look a row is driving. Desktop only in effect — there is no
    // hover on the mobile sheet, and the look is behind a scrim there anyway.
    this.sheetRoot.querySelectorAll('[data-lb-row]').forEach((row) => {
      const other = row.dataset.lbRow === '1' ? '2' : '1';
      row.addEventListener('mouseenter', () => this.setAttribute('data-lb-dim', other));
      row.addEventListener('mouseleave', () => this.removeAttribute('data-lb-dim'));
    });

    this.handleSheetKeydown = this.handleSheetKeydown.bind(this);
    this.initSheetDrag();

    // The desktop panel is always present, so it must not carry `hidden`.
    // Kept in sync rather than set once: the theme editor and a rotated
    // tablet both cross this line without a reload.
    this.sheetLayoutQuery = window.matchMedia(SHEET_LAYOUT_QUERY);
    const syncLayoutMode = () => {
      const overlay = this.sheetLayoutQuery.matches;
      this.sheetRoot.hidden = overlay && !this.sheetIsOpen;
      // Modal only when it actually covers the page.
      if (overlay) this.sheet?.setAttribute('aria-modal', 'true');
      else this.sheet?.removeAttribute('aria-modal');
    };
    syncLayoutMode();
    this.sheetLayoutQuery.addEventListener('change', syncLayoutMode);

    // base.css puts view-transition-name on <main> during a page-navigation
    // transition, which briefly makes it a containing block for fixed and
    // would snap an open sheet into <main>'s box. Closing on the way out
    // sidesteps it, and a sheet left open across bfcache restore would be
    // stale anyway.
    window.addEventListener('pagehide', () => this.closeSheet());
  }

  openSheet() {
    if (!this.sheetRoot || this.sheetIsOpen) return;

    // A close still settling would otherwise set hidden on the sheet we are
    // about to open.
    clearTimeout(this.sheetHideTimer);

    this.syncSheet();
    this.sheetLastFocused = document.activeElement;
    this.sheetRoot.hidden = false;
    this.sheetRoot.setAttribute('aria-hidden', 'false');

    // The browser has to have computed the closed position before the open
    // one is applied, or the two style changes coalesce and the sheet appears
    // already-open instead of rising. Reading offsetHeight forces that layout
    // synchronously.
    //
    // Deliberately not the two-nested-requestAnimationFrame version used by
    // the PDP's size-guide sheet: rAF callbacks don't run while the page
    // isn't producing frames, and if they never run the sheet is left
    // displayed-but-closed — a full-viewport invisible overlay at z-index 120
    // that swallows every tap beneath it. That is the same failure this
    // sheet already shipped once through a CSS specificity slip, and it isn't
    // worth leaving a second route to it open for the sake of avoiding one
    // forced reflow on a deliberate tap.
    void this.sheet.offsetHeight;
    this.sheetRoot.classList.add('is-open');

    // The state the whole section reads: mobile raises the sheet off the
    // bottom, desktop slides the panel in from the right and opens the look
    // out from stacked to side by side. One flag, because it is one idea —
    // the shopper has moved from looking to choosing.
    this.dataset.lbState = 'custom';

    this.sheetIsOpen = true;
    document.addEventListener('keydown', this.handleSheetKeydown);

    // First thing worth reaching, by layout rather than by breakpoint: the
    // chip on mobile, the back button on desktop where chips are display:
    // none. Picking by selector order alone matched a chip that renders on
    // neither — it has no box on desktop — and focus went nowhere.
    const candidates = [
      ...this.sheetRoot.querySelectorAll('[data-lb-chip]'),
      this.querySelector('[data-lb-close]'),
      ...this.sheetRoot.querySelectorAll('[data-lb-proxy]'),
    ];
    // preventScroll is load-bearing, not politeness. On desktop the winning
    // candidate is the back button INSIDE the panel, and at this instant the
    // panel is still parked at translateX(100%) — outside the stage, clipped
    // by the section's overflow: hidden. But overflow: hidden only hides the
    // scrollbar and blocks the *user*; the box is still a scroll container,
    // and the default focus behaviour scrolls a focused descendant into view
    // by setting scrollLeft on it. Scroll offset is not an animatable
    // property, so that landed as an instant sideways jump of everything on
    // the stage — garments included — one tick after the 0.6s transitions
    // started, which then played out from the displaced position. It read as
    // the look blinking to side-by-side and the panel snapping open before
    // either had animated anywhere.
    const first = candidates.find((el) => el instanceof HTMLElement && el.getClientRects().length);
    first?.focus({ preventScroll: true });
  }

  /**
   * The guide still replaces the ledger inside the same sheet element
   * rather than stacking a second sheet in the DOM (see the CSS comment on
   * .lookbuilder-guide-view for why), but now rises into place with the
   * same translateY(100%) → 0 idiom the outer sheet uses on itself, so it
   * reads as a second sheet opening on top of the one already open.
   * [data-lb-view='guide'] mounts it (display: flex, still parked
   * off-screen) for as long as it's either open or animating closed, and
   * .is-guide-open is what actually raises it — added a frame after
   * mounting so the transform has a starting position to animate from,
   * same two-step reasoning as openSheet(). Closing removes .is-guide-open
   * immediately, which — because the CSS puts the quicker transition on
   * the base rule and the slower one only on .is-guide-open — is also what
   * makes the close read as a faster reverse of the open, but leaves
   * data-lb-view set until GUIDE_CLOSE_TRANSITION_MS later so the guide
   * stays mounted for that exit transition instead of vanishing instantly.
   */
  openGuide() {
    if (!this.sheet) return;
    clearTimeout(this.guideHideTimer);
    this.sheet.dataset.lbView = 'guide';
    this.sheet.setAttribute('aria-label', 'Size guide');
    // The ledger's scroll position has no bearing on the guide's own
    // (separate) content, but leaving it non-zero would show up as a jump
    // the moment the ledger becomes the visible view again on close.
    this.sheet.scrollTop = 0;
    void this.sheet.offsetHeight;
    this.sheet.classList.add('is-guide-open');
    this.guideBackBtn?.focus();
  }

  closeGuide() {
    if (!this.sheet) return;
    this.sheet.classList.remove('is-guide-open');
    this.sheet.setAttribute('aria-label', 'Build your look');
    clearTimeout(this.guideHideTimer);
    this.guideHideTimer = setTimeout(() => {
      delete this.sheet.dataset.lbView;
    }, GUIDE_CLOSE_TRANSITION_MS);
    this.guideOpenLink?.focus();
  }

  closeSheet() {
    if (!this.sheetRoot || !this.sheetIsOpen) return;

    this.sheetRoot.classList.remove('is-open');
    this.sheetIsOpen = false;
    this.dataset.lbState = 'look';
    this.removeAttribute('data-lb-dim');
    this.sheetRoot.setAttribute('aria-hidden', 'true');
    document.removeEventListener('keydown', this.handleSheetKeydown);

    // Silently, not through closeGuide(): reopening should always land on
    // the ledger, but the sheet is on its way off-screen and shouldn't have
    // its aria-label swap or a focus move fighting sheetLastFocused below.
    // Also clears any pending guide-unmount timer and its .is-guide-open
    // state outright rather than letting it play out — the whole sheet is
    // already leaving, so the guide's own exit transition would never be
    // seen anyway.
    clearTimeout(this.guideHideTimer);
    if (this.sheet) {
      delete this.sheet.dataset.lbView;
      this.sheet.classList.remove('is-guide-open');
      this.sheet.setAttribute('aria-label', 'Build your look');
    }

    // Any drag offset left on the sheet has to be cleared or the next open
    // starts partway down.
    if (this.sheet) this.sheet.style.transform = '';

    // A timer rather than transitionend. transitionend doesn't fire at all if
    // the transition is removed (prefers-reduced-motion, a print stylesheet,
    // a browser that isn't animating), and the old listener was re-added on
    // every close without ever being removed in that case — so it both leaked
    // and left `hidden` unset. The timer always lands, and .is-open has
    // already taken the sheet out of the hit-test path by this point, so the
    // exact moment `hidden` follows is cosmetic.
    clearTimeout(this.sheetHideTimer);
    this.sheetHideTimer = setTimeout(() => {
      // Only the overlay is taken out of the page; the desktop panel slides
      // back off-stage and stays, clipped by the section's overflow.
      if (!this.sheetIsOpen && this.sheetLayoutQuery?.matches) this.sheetRoot.hidden = true;
    }, SHEET_TRANSITION_MS);

    // preventScroll for the same reason as the open path: the closing panel is
    // on its way back off-stage, and any scroll the browser performs to serve
    // this focus move would jump the stage out from under the transition.
    if (this.sheetLastFocused instanceof HTMLElement) this.sheetLastFocused.focus({ preventScroll: true });
  }

  handleSheetKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      // One level at a time: out of the guide first, then the whole sheet.
      if (this.sheet?.dataset.lbView === 'guide') this.closeGuide();
      else this.closeSheet();
      return;
    }

    // Trap only while the ledger is the modal overlay. The desktop panel sits
    // beside the look rather than over it, so the rest of the page is still
    // legitimately reachable and trapping there would strand the shopper.
    if (event.key !== 'Tab' || !this.sheet || !this.sheetLayoutQuery?.matches) return;

    // The page behind the sheet is still in the DOM and still focusable, so
    // without a trap, tabbing walks into content the shopper can't see.
    // getClientRects() rather than offsetParent: offsetParent is null for
    // fixed-position elements whether or not they're visible.
    const focusable = Array.from(
      this.sheet.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')
    ).filter((el) => el.getClientRects().length > 0);
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  initSheetDrag() {
    const handle = this.sheetRoot.querySelector('[data-lb-sheet-handle]');
    if (!handle || !this.sheet) return;

    // Past this much travel, release the sheet instead of snapping back…
    const DISMISS_DISTANCE = 90;
    // …or below it if the flick was fast enough to read as intent.
    const DISMISS_VELOCITY = 0.5;

    let startY = 0;
    let startTime = 0;
    let offset = 0;
    let dragging = false;

    handle.addEventListener('touchstart', (e) => {
      startY = e.touches[0].clientY;
      startTime = e.timeStamp;
      offset = 0;
      dragging = true;
      this.sheet.style.transition = 'none';
    }, { passive: true });

    handle.addEventListener('touchmove', (e) => {
      if (!dragging) return;
      // Downward only — dragging up shouldn't lift the sheet off its stop.
      offset = Math.max(0, e.touches[0].clientY - startY);
      this.sheet.style.transform = `translateY(${offset}px)`;
    }, { passive: true });

    const endDrag = (e) => {
      if (!dragging) return;
      dragging = false;
      this.sheet.style.transition = '';

      const elapsed = Math.max(1, e.timeStamp - startTime);
      if (offset > DISMISS_DISTANCE || offset / elapsed > DISMISS_VELOCITY) {
        this.closeSheet();
      } else {
        this.sheet.style.transform = '';
      }
    };

    handle.addEventListener('touchend', endDrag);
    handle.addEventListener('touchcancel', endDrag);
  }

  /** Accordion: opening one drawer closes every other, as in the mock. */
  toggleDrawer(chip) {
    const willOpen = chip.getAttribute('aria-expanded') !== 'true';
    const kind = chip.dataset.lbChip;
    const row = chip.closest('[data-lb-row]');

    this.sheetRoot.querySelectorAll('[data-lb-chip]').forEach((other) => {
      other.setAttribute('aria-expanded', 'false');
    });
    this.sheetRoot.querySelectorAll('[data-lb-pop]').forEach((pop) => {
      pop.classList.remove('is-open');
    });

    if (!willOpen) return;

    chip.setAttribute('aria-expanded', 'true');
    row?.querySelector(`[data-lb-pop="${kind}"]`)?.classList.add('is-open');
  }

  /** Write a proxy's choice into the real radio it stands for. */
  activateProxy(proxy) {
    const radio = this.querySelector(`#${CSS.escape(proxy.dataset.lbProxy)}`);
    if (!radio || radio.disabled) return;

    radio.checked = true;
    radio.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * Desktop only: slides the outgoing colourway out and the incoming one in,
   * rather than cutting between them. Mobile keeps its real scroll track,
   * which already slides natively.
   *
   * The slides are absolutely stacked, so .is-active alone just swaps which
   * one is visible; this layers a Web Animations transform on top, holding
   * both visible (opacity 1) only for the animation's duration. Nothing is
   * left behind when it finishes: without fill, the stylesheet takes back
   * over and the outgoing slide is simply the non-active, hidden one again.
   *
   * Rapid steps chain like a strip rather than restarting. Every slide still
   * mid-animation is picked up from where it currently is, the incoming one
   * is placed one track-width beyond the current slide (or left where it is,
   * if it's already on screen — a quick reversal), and all of them move by
   * the same distance with the same curve, so neighbours never overlap or
   * open a gap between them.
   *
   * @param {string} column - '1' or '2'.
   * @param {HTMLElement} track - The column's .lookbuilder-carousel.
   * @param {HTMLElement} from - The slide being left.
   * @param {HTMLElement} to - The slide becoming active.
   * @param {number} direction - 1 to travel forward (enter from the right),
   *   -1 to travel back (enter from the left).
   */
  animateColorwaySlide(column, track, from, to, direction) {
    const running = this.slideAnims[column];

    if (this.sheetLayoutQuery.matches || this.reducedMotionQuery.matches) {
      running.forEach((anim) => anim.cancel());
      running.clear();
      return;
    }

    const width = track.clientWidth;
    const positions = new Map();
    running.forEach((anim, el) => {
      // A finished animation can still be listed here until its 'finish'
      // event fires on the next frame; by then it has no effect, so its
      // slide is back to hidden, not a neighbour to carry along.
      if (anim.playState !== 'finished') {
        positions.set(el, new DOMMatrixReadOnly(getComputedStyle(el).transform).m41);
      }
      anim.cancel();
    });
    running.clear();

    if (!positions.has(from)) positions.set(from, 0);
    if (!positions.has(to)) positions.set(to, positions.get(from) + direction * width);
    const shift = -positions.get(to);

    positions.forEach((x, el) => {
      const anim = el.animate(
        [
          { transform: `translateX(${x}px)`, opacity: 1 },
          { transform: `translateX(${x + shift}px)`, opacity: 1 }
        ],
        { duration: COLORWAY_SLIDE_MS, easing: COLORWAY_SLIDE_EASING }
      );
      running.set(el, anim);
      anim.addEventListener('finish', () => {
        if (running.get(el) === anim) running.delete(el);
      });
    });
  }

  /** The active slide for a column — the one the carousel is settled on. */
  activeSlide(column) {
    if (this.activeSlideEl[column]) return this.activeSlideEl[column];

    // :not([data-clone]) matters before activeSlideEl is ever set (first
    // paint, before any scroll/selection has happened): setupColorwayLoopClones
    // prepends head clones before the real first slide, so a bare
    // .lookbuilder-slide query would return one of those instead.
    const col = this.querySelector(`.lookbuilder-column[data-column="${column}"]`);
    return col?.querySelector('.lookbuilder-slide:not([data-clone])') || null;
  }

  /** Rebuild the sheet from the real controls. Safe to call any number of times. */
  syncSheet() {
    if (!this.sheetRoot) return;

    this.sheetRoot.querySelectorAll('[data-lb-row]').forEach((row) => {
      const column = row.dataset.lbRow;
      const slide = this.activeSlide(column);
      if (!slide) return;

      const thumb = row.querySelector('[data-lb-thumb]');
      const firstImage = slide.querySelector('.lookbuilder-product-image');
      if (thumb && firstImage && thumb.getAttribute('src') !== firstImage.src) {
        thumb.src = firstImage.src;
      }

      // The product title carries the colourway ("… — Cobalt"), so it changes
      // with the slide; the block setting beside it is the stable garment
      // name. Desktop shows both, mobile only the stable one.
      const title = row.querySelector('[data-lb-title]');
      if (title) title.textContent = slide.dataset.productTitle || '';

      // Per-line prices stay as Liquid's own `| money` output. Only the total
      // is computed, because only the total has no server-rendered form.
      const slidePrice = slide.querySelector('.lookbuilder-product-price');
      const priceText = slidePrice ? slidePrice.textContent.trim() : '';
      row.querySelectorAll('[data-lb-price], [data-lb-line-price]').forEach((el) => {
        el.textContent = priceText;
      });

      // Rebuild the proxies only when the colourway actually changed —
      // otherwise a sync triggered by a size tap would replace the button
      // under the shopper's finger and drop focus mid-interaction.
      if (this.renderedSlide[column] !== slide) {
        this.renderProxies(row, slide, column);
        this.renderedSlide[column] = slide;
      }
      this.refreshProxyStates(row, slide);
    });

    this.syncTotals();
  }

  /**
   * The entry card's garment line and both copies of the total.
   *
   * Prices are summed in minor units and formatted once, never by adding
   * rendered money strings together — those carry symbols, separators and a
   * currency that varies per market.
   */
  syncTotals() {
    const slides = ['1', '2'].map((column) => this.activeSlide(column)).filter(Boolean);
    if (!slides.length) return;

    const cents = slides.reduce((sum, slide) => sum + (Number(slide.dataset.priceCents) || 0), 0);
    const format = this.dataset.moneyFormat || '{{amount}}';
    const currency = this.dataset.currency || 'USD';

    let total = '';
    try {
      total = formatMoney(cents, format, currency);
    } catch (error) {
      // A malformed format string shouldn't cost the shopper the button. The
      // total is the only casualty; every other price on the page is
      // server-rendered.
      console.error(error);
    }

    this.querySelectorAll('[data-lb-total], [data-lb-cta-total]').forEach((el) => {
      el.textContent = total;
    });
  }

  renderProxies(row, slide, column) {
    const colorBody = row.querySelector('[data-lb-pop="color"] [data-lb-pop-body]');
    const sizeBody = row.querySelector('[data-lb-pop="size"] [data-lb-pop-body]');
    const col = this.querySelector(`.lookbuilder-column[data-column="${column}"]`);

    if (colorBody) {
      // Every slide lists the whole colourway set, so the active one is as
      // good a source as any.
      colorBody.replaceChildren(
        ...Array.from(slide.querySelectorAll('.lookbuilder-color-radio')).map((radio) => {
          const label = col.querySelector(`label[for="${CSS.escape(radio.id)}"]`);
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'lookbuilder-proxy-swatch';
          btn.dataset.lbProxy = radio.id;
          // Which colourway this stands for, as a slide index. The proxy's
          // selected state is decided by comparing this against the active
          // slide, never by reading the radio's own `checked` — see the note
          // on activeSlideEl for why that would be wrong.
          btn.dataset.lbSlideIndex = radio.dataset.slideIndex;
          btn.setAttribute('role', 'radio');
          // radio.value FIRST, not label?.title — value is sib_color_val
          // from the liquid loop above, the product's actual Color/Colour
          // option (already falling back to the product's title on the
          // LIQUID side if no such option exists, so it's never blank).
          // label.title is always the PRODUCT's title regardless — every
          // product has one, so `label?.title || radio.value` almost never
          // reached the fallback, and this aria-label was effectively
          // always the full product name ("Le Signature Hoodie") rather
          // than the colour ("Cobalt"). Invisible while this was
          // screen-reader-only text; not invisible once it became the
          // segmented trigger's visible label.
          btn.setAttribute('aria-label', radio.value || label?.title || '');
          btn.style.backgroundColor = label?.style.backgroundColor || '';
          return btn;
        })
      );
    }

    if (sizeBody) {
      const sizeRadios = Array.from(slide.querySelectorAll('.lookbuilder-size-radio'));
      // Drives the equal-width grid in section-shabibi-lookbuilder.css — the
      // CSS equivalent of --size-cols on the PDP, set here instead of by
      // Liquid because the count can differ between this look's two
      // garments and the pad is rebuilt on every slide change anyway.
      sizeBody.style.setProperty('--lb-size-cols', String(sizeRadios.length || 1));
      sizeBody.replaceChildren(
        ...sizeRadios.map((radio) => {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'lookbuilder-proxy-size';
          btn.dataset.lbProxy = radio.id;
          btn.setAttribute('role', 'radio');
          btn.textContent = radio.value;
          return btn;
        })
      );
    }
  }

  refreshProxyStates(row, slide) {
    const colorChip = row.querySelector('[data-lb-chip="color"]');
    const sizeChip = row.querySelector('[data-lb-chip="size"]');
    const dot = row.querySelector('[data-lb-chip-dot]');
    const colorText = row.querySelector('[data-lb-chip-color]');
    const sizeText = row.querySelector('[data-lb-chip-size]');

    const activeIndex = slide.dataset.slideIndex;

    row.querySelectorAll('[data-lb-proxy]').forEach((proxy) => {
      const radio = this.querySelector(`#${CSS.escape(proxy.dataset.lbProxy)}`);
      if (!radio) return;

      if (proxy.classList.contains('lookbuilder-proxy-swatch')) {
        proxy.setAttribute('aria-checked', String(proxy.dataset.lbSlideIndex === activeIndex));
        return;
      }

      proxy.setAttribute('aria-checked', String(radio.checked));
      // Sold out is communicated, not hidden: the size exists and can still
      // be wanted, so it stays legible and struck through.
      proxy.disabled = radio.disabled;
      proxy.dataset.state = radio.disabled ? 'soldout' : 'available';
    });

    // The chip's dot and the desktop summary's dot say the same thing in two
    // presentations, so they're written together rather than by two paths.
    const activeSwatch = row.querySelector(`[data-lb-slide-index="${activeIndex}"]`);
    if (activeSwatch) {
      const swatchColor = activeSwatch.style.backgroundColor || '';
      [dot, row.querySelector('[data-lb-sum-dot]')].forEach((el) => {
        if (el) el.style.backgroundColor = swatchColor;
      });
      const colorName = activeSwatch.getAttribute('aria-label') || '';
      // The dot alone was the whole desktop trigger before this pass — the
      // segmented-pill redesign shows the colour's name as text too, reusing
      // the same aria-label the dot's own accessible name already comes
      // from rather than adding a second source of truth for it.
      if (colorText) colorText.textContent = colorName;
      colorChip?.setAttribute('aria-label', `Colour: ${colorName}`);
    }

    const activeSize = slide.querySelector('.lookbuilder-size-radio:checked');
    if (sizeText) sizeText.textContent = activeSize ? activeSize.value : 'Size';

    // An em dash, not "Size": desktop's summary is a readout of what's been
    // chosen, so an unmade choice should read as blank rather than as a label.
    const sumSize = row.querySelector('[data-lb-sum-size]');
    if (sumSize) {
      sumSize.textContent = activeSize ? activeSize.value : '—';
      sumSize.classList.toggle('is-pending', !activeSize);
    }
    if (sizeChip) {
      sizeChip.dataset.empty = String(!activeSize);
      // Distinct from "empty": an unmade choice still gets the full dim
      // treatment if nothing on this slide can be picked, but reads at full
      // ink (not dimmed) when there's actually a size to tap — lets the
      // shopper tell before opening the drawer instead of only after.
      // Radios are disabled by updateVariantForSlide for sold-out sizes, so
      // this is live per slide.
      const hasAvailableSize = Array.from(slide.querySelectorAll('.lookbuilder-size-radio')).some(
        (radio) => !radio.disabled
      );
      sizeChip.dataset.available = String(hasAvailableSize);
      sizeChip.setAttribute('aria-label', activeSize ? `Size: ${activeSize.value}` : 'Choose a size');
    }
  }

  // Helper: collect all cart-items-component section IDs
  getCartSectionIds() {
    const ids = [];
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el.dataset && el.dataset.sectionId) ids.push(el.dataset.sectionId);
    });
    return ids;
  }

  // Helper: fetch full cart
  async fetchCart() {
    const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
    return res.json();
  }

  // Helper: dispatch proper cart event
  dispatchCartUpdate(cart, sections, quantity) {
    document.dispatchEvent(new CustomEvent('cart:update', {
      bubbles: true,
      detail: {
        resource: cart,
        data: {
          source: 'product-form-component',
          itemCount: quantity,
          sections: sections || {}
        }
      }
    }));
  }

  async handleAddBundle() {
    if (!this.state.column1_variant || !this.state.column2_variant) return;

    this.setCtaState('busy');

    const items = [
      { id: this.state.column1_variant, quantity: 1 },
      { id: this.state.column2_variant, quantity: 1 }
    ];

    try {
      // Merge identical items just in case both columns have the same variant
      const mergedItems = {};
      items.forEach(item => {
        if (mergedItems[item.id]) {
          mergedItems[item.id].quantity += item.quantity;
        } else {
          mergedItems[item.id] = { id: item.id, quantity: item.quantity };
        }
      });
      
      const payload = {
        items: Object.values(mergedItems).map(item => ({
          id: Number(item.id),
          quantity: item.quantity
        }))
      };
      
      const sectionIds = this.getCartSectionIds();
      if (sectionIds.length > 0) {
        payload.sections = sectionIds.join(',');
      }

      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.description || errData.message || 'Failed to add to cart');
      }

      const addedData = await response.json();
      
      // 2. Fetch full cart
      const cart = await this.fetchCart();
      
      // 3. Dispatch detailed update event
      // If addedData is an object with items array (since we added multiple), take sections from it
      this.dispatchCartUpdate(cart, addedData.sections, Object.values(mergedItems).reduce((acc, item) => acc + item.quantity, 0));

      // Out of the way before the drawer arrives — two stacked overlays read
      // as a stuck sheet, and the drawer is now the thing to act on.
      this.closeSheet();

      const cartDrawer = document.querySelector('cart-drawer-component');
      if (cartDrawer && typeof cartDrawer.showDialog === 'function') {
        cartDrawer.showDialog();
      } else if (cartDrawer && typeof cartDrawer.open === 'function') {
        cartDrawer.open();
      }

      this.setCtaState('done');

      if (typeof window.confetti === 'function') {
        window.confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 }
        });
      }

      setTimeout(() => {
        this.updateButtonState();
      }, 2000);

    } catch (error) {
      console.error(error);
      this.setCtaState('error');
      setTimeout(() => {
        this.updateButtonState();
      }, 3000);
    }
  }
}

// First-visit hint that the garments swipe, on the mobile/sheet layouts.
//
// Those layouts hide the < > colourway arrows (swiping the carousel is the
// control there), which leaves nothing on screen saying a garment can be
// swiped. Once per session, shortly after the top garment comes into view,
// it slides partway toward the next colourway, so the neighbour's edge
// peeks in, and settles back. Same rules as the PDP's lift demo
// (assets/shabibi-pdp-lift.js), whose timings and curve this reuses so the
// two hints move alike: never under reduced motion, never into a
// background tab, and any input at all cancels it and hands over.
//
// A transform on the slides rather than a scroll of the track: a scroll
// would run handleScrollEnd, which reselects the colourway and variant
// under the shopper. A transform moves the same pixels and touches no
// state — provided the track's scroll-snap is off while it plays. Chrome
// re-snaps a mandatory snap container to its areas' transformed positions,
// so with snap on, the track scrolled along with the slides and exactly
// cancelled the movement (measured: 716 → 626 for a 90px nudge), and that
// scroll then tripped this hint's own input detection.
const SWIPE_HINT_KEY = 'shabibi:lookbuilder-swipe-hint';
const SWIPE_HINT_DELAY = 900;
// Fraction of the track's width the garment travels. Garments are cut-outs
// centred in their slide, so the neighbour's first ~quarter is mostly
// background; this is enough for its edge to show, not just empty space.
const SWIPE_HINT_TRAVEL = 0.25;
const SWIPE_HINT_OUT = 520;
const SWIPE_HINT_HOLD = 180;
const SWIPE_HINT_RETURN = 620;
const SWIPE_HINT_EASE = 'cubic-bezier(0.23, 1, 0.32, 1)';
// On cancel, the slides ease home this fast rather than jumping, since the
// shopper's own gesture is starting on top of them.
const SWIPE_HINT_HANDOVER = 160;
// The neighbour's image is lazy-loaded; peeking at an empty card teaches
// nothing, so wait for it, but not indefinitely.
const SWIPE_HINT_IMAGE_TIMEOUT = 3000;

class ColorwaySwipeHint {
  /**
   * @param {ShabibiLookbuilder} lookbuilder
   * @param {string} column - Which garment demonstrates; '1' is the top one.
   */
  constructor(lookbuilder, column = '1') {
    this.lookbuilder = lookbuilder;
    this.column = column;
    this.track = lookbuilder.querySelector(`.lookbuilder-column[data-column="${column}"] .lookbuilder-carousel`);
    if (!this.track) return;

    this.animations = [];
    this.timer = null;
    this.observer = null;
    this.listening = false;

    this.cancel = this.cancel.bind(this);
    this.handleVisibility = this.handleVisibility.bind(this);

    if (!this.canHint()) return;

    // "Lands there" is when the top garment is mostly on screen, not page
    // load: the section can sit below other content, and a hint played
    // off-screen spends the one shot on nobody.
    this.observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.observer.disconnect();
        this.observer = null;
        this.schedule();
      },
      { threshold: 0.6 }
    );
    this.observer.observe(this.track);
  }

  canHint() {
    if (!this.lookbuilder.sheetLayoutQuery.matches) return false;
    if (this.lookbuilder.reducedMotionQuery.matches) return false;
    if (this.lookbuilder.sheetRoot?.classList.contains('is-open')) return false;
    return !this.flagged();
  }

  schedule() {
    // Listening starts with the delay, so a swipe during the wait cancels
    // the hint rather than being demonstrated at afterwards.
    this.listen();

    this.timer = window.setTimeout(() => {
      this.timer = null;
      if (!this.canHint()) {
        this.finish();
        return;
      }
      if (document.visibilityState !== 'visible') {
        document.addEventListener('visibilitychange', this.handleVisibility);
        return;
      }
      this.prepare();
    }, SWIPE_HINT_DELAY);
  }

  handleVisibility() {
    if (document.visibilityState !== 'visible') return;
    document.removeEventListener('visibilitychange', this.handleVisibility);
    if (!this.canHint()) {
      this.finish();
      return;
    }
    this.prepare();
  }

  prepare() {
    const current = this.lookbuilder.activeSlide(this.column);
    // The next sibling, clone or not: past the last colourway it's the loop
    // clone of the first, which is exactly what a real swipe would reveal.
    const next = current?.nextElementSibling;
    if (!current || !next) {
      this.finish();
      return;
    }

    const image = next.querySelector('img');
    if (!image || (image.complete && image.naturalWidth > 0)) {
      this.run(current, next);
      return;
    }

    image.loading = 'eager';
    const timeout = window.setTimeout(() => settle(false), SWIPE_HINT_IMAGE_TIMEOUT);
    const settle = (loaded) => {
      window.clearTimeout(timeout);
      image.removeEventListener('load', onLoad);
      image.removeEventListener('error', onError);
      // Re-checked: the wait can outlast the shopper's patience, and they
      // may have swiped, opened the sheet, or rotated meanwhile.
      if (!this.listening) return;
      if (loaded && this.canHint()) this.run(current, next);
      else this.finish();
    };
    const onLoad = () => settle(true);
    const onError = () => settle(false);
    image.addEventListener('load', onLoad);
    image.addEventListener('error', onError);
  }

  run(current, next) {
    const distance = Math.round(this.track.clientWidth * SWIPE_HINT_TRAVEL);
    const total = SWIPE_HINT_OUT + SWIPE_HINT_HOLD + SWIPE_HINT_RETURN;
    const keyframes = [
      { transform: 'translateX(0)', easing: SWIPE_HINT_EASE },
      { transform: `translateX(${-distance}px)`, offset: SWIPE_HINT_OUT / total },
      {
        transform: `translateX(${-distance}px)`,
        offset: (SWIPE_HINT_OUT + SWIPE_HINT_HOLD) / total,
        easing: SWIPE_HINT_EASE
      },
      { transform: 'translateX(0)' }
    ];

    // See the note at the top of this class. Restored in finish(), once
    // the slides are back at rest, so re-enabling it moves nothing.
    this.track.style.scrollSnapType = 'none';

    // Only these two can be on screen: the neighbour on the other side is
    // moving further out of view.
    this.animations = [current, next].map((slide) => slide.animate(keyframes, { duration: total }));
    this.animations[0].finished.then(() => this.finish(), () => {});
  }

  listen() {
    if (this.listening) return;
    this.listening = true;
    document.addEventListener('pointerdown', this.cancel);
    document.addEventListener('touchstart', this.cancel, { passive: true });
    document.addEventListener('wheel', this.cancel, { passive: true });
    document.addEventListener('keydown', this.cancel);
    // The hint itself never scrolls the track, so any scroll is the shopper.
    this.track.addEventListener('scroll', this.cancel, { passive: true });
  }

  unlisten() {
    if (!this.listening) return;
    this.listening = false;
    document.removeEventListener('pointerdown', this.cancel);
    document.removeEventListener('touchstart', this.cancel);
    document.removeEventListener('wheel', this.cancel);
    document.removeEventListener('keydown', this.cancel);
    this.track.removeEventListener('scroll', this.cancel);
  }

  cancel() {
    const handovers = [];
    this.animations.forEach((animation) => {
      if (animation.playState === 'finished') return;
      const slide = /** @type {KeyframeEffect} */ (animation.effect).target;
      const from = getComputedStyle(slide).transform;
      animation.cancel();
      handovers.push(
        slide.animate([{ transform: from }, { transform: 'translateX(0)' }], {
          duration: SWIPE_HINT_HANDOVER,
          easing: SWIPE_HINT_EASE
        }).finished
      );
    });

    // Snap comes back once the slides are home, not now: re-enabled while
    // they're still offset, it would re-snap the track to the offset (see
    // the class note) under the shopper's finger. Snapping applies when a
    // gesture ends, so their swipe still lands on a colourway.
    this.finish(!handovers.length);
    if (handovers.length) {
      Promise.all(handovers).then(
        () => this.restoreSnap(),
        () => this.restoreSnap()
      );
    }
  }

  /** @param {boolean} [restoreSnap] - False while a handover is still easing home. */
  finish(restoreSnap = true) {
    if (this.timer !== null) {
      window.clearTimeout(this.timer);
      this.timer = null;
    }
    this.observer?.disconnect();
    this.observer = null;
    document.removeEventListener('visibilitychange', this.handleVisibility);
    this.animations = [];
    this.unlisten();
    if (restoreSnap) this.restoreSnap();
    this.flag();
  }

  restoreSnap() {
    this.track.style.scrollSnapType = '';
  }

  // sessionStorage throws rather than no-oping in Safari private mode; worst
  // case the hint plays once more than intended.
  flagged() {
    try {
      return window.sessionStorage.getItem(SWIPE_HINT_KEY) === '1';
    } catch (error) {
      return false;
    }
  }

  flag() {
    try {
      window.sessionStorage.setItem(SWIPE_HINT_KEY, '1');
    } catch (error) {
      // See flagged().
    }
  }
}

if (!customElements.get('shabibi-lookbuilder')) {
  customElements.define('shabibi-lookbuilder', ShabibiLookbuilder);
}
